import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Zap,
  Target,
  Share2,
  Ruler,
  CloudUpload,
  Languages,
  ArrowRight,
  CheckCircle2,
  Globe as GlobeIcon,
  Sun as SunIcon,
  Moon as MoonIcon,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useTheme } from "../hooks/useTheme";
import { setLanguage, type SupportedLang } from "../i18n";

/**
 * Public marketing landing.
 * Auth-first funnel: Create account / Sign in. No waitlist, no guest path.
 */
export function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [theme, , toggleTheme] = useTheme();
  const currentLang = (i18n.language?.slice(0, 2) ?? "en") as SupportedLang;

  useEffect(() => {
    if (!loading && session) navigate("/app", { replace: true });
  }, [loading, session, navigate]);

  const toggleLang = async () => {
    await setLanguage(currentLang === "ar" ? "en" : "ar");
  };

  const stats = [
    { k: t("landing.stat1"), v: t("landing.stat1Label") },
    { k: t("landing.stat2"), v: t("landing.stat2Label") },
    { k: t("landing.stat3"), v: t("landing.stat3Label") },
  ];
  const features = [
    { Icon: Zap, title: t("landing.feature1Title"), body: t("landing.feature1Body") },
    { Icon: Target, title: t("landing.feature2Title"), body: t("landing.feature2Body") },
    { Icon: Share2, title: t("landing.feature3Title"), body: t("landing.feature3Body") },
    { Icon: Ruler, title: t("landing.feature4Title"), body: t("landing.feature4Body") },
    { Icon: CloudUpload, title: t("landing.feature5Title"), body: t("landing.feature5Body") },
    { Icon: Languages, title: t("landing.feature6Title"), body: t("landing.feature6Body") },
  ];
  const useCases = [
    t("landing.useCase1"),
    t("landing.useCase2"),
    t("landing.useCase3"),
    t("landing.useCase4"),
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950">
      {/* Sticky header */}
      <header className="sticky top-0 z-20 border-b border-slate-200/70 bg-white/80 backdrop-blur dark:border-slate-800/70 dark:bg-slate-950/70">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="text-xl font-bold text-brand-700 dark:text-brand-100">
            {t("app.name")}
          </div>
          <nav className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Light mode" : "Dark mode"}
              title={theme === "dark" ? "Light mode" : "Dark mode"}
              className="flex h-9 w-9 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              {theme === "dark" ? (
                <SunIcon className="h-4 w-4" />
              ) : (
                <MoonIcon className="h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={toggleLang}
              aria-label={currentLang === "ar" ? "English" : "العربية"}
              title={currentLang === "ar" ? "English" : "العربية"}
              className="flex h-9 w-auto items-center gap-1 rounded-md px-2 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              <GlobeIcon className="h-4 w-4" />
              {currentLang === "ar" ? "EN" : "ع"}
            </button>
            <Link to="/login" className="btn-ghost text-sm">
              {t("nav.signIn")}
            </Link>
            <Link to="/signup" className="btn-primary text-sm">
              {t("nav.createAccount")}
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 -z-10"
        >
          <div className="absolute left-1/2 top-0 h-[500px] w-[900px] -translate-x-1/2 rounded-full bg-gradient-to-br from-brand-100/70 via-blue-100/40 to-transparent blur-3xl dark:from-brand-900/30 dark:via-brand-700/20" />
          <div className="absolute right-0 top-40 h-[300px] w-[300px] rounded-full bg-emerald-100/40 blur-3xl dark:bg-emerald-900/20" />
        </div>

        <div className="mx-auto max-w-5xl px-6 pb-16 pt-12 sm:pb-24 sm:pt-20 lg:pt-28">
          <div className="mx-auto max-w-3xl text-center">


            <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-slate-900 dark:text-white sm:text-6xl">
              {t("landing.heroTitle")}
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-600 dark:text-slate-300 sm:text-xl">
              {t("landing.heroSubtitle")}
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link
                to="/signup"
                className="btn-primary inline-flex w-full items-center justify-center gap-2 min-h-[52px] text-base sm:w-auto sm:px-6"
              >
                {t("landing.ctaPrimary")}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/login"
                className="btn-ghost w-full min-h-[52px] text-base sm:w-auto sm:px-5"
              >
                {t("landing.ctaSecondary")}
              </Link>
            </div>

            <p className="mt-4 text-sm text-slate-500 dark:text-slate-400">
              {t("landing.noCardCopy")}
            </p>
          </div>

          {/* Editor preview card */}
          <div className="mt-14 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl shadow-slate-200/50 dark:border-slate-800 dark:bg-slate-900 dark:shadow-black/40 sm:mt-20">
            <EditorPreview />
          </div>
        </div>
      </section>

      {/* Stats band */}
      <section className="border-y border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 px-6 py-10 sm:grid-cols-3">
          {stats.map((s) => (
            <div key={s.v} className="text-center">
              <p className="font-mono text-3xl font-bold text-brand-700 dark:text-brand-100 sm:text-4xl">
                {s.k}
              </p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-5xl px-6 py-20">
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map(({ Icon, title, body }) => (
            <li
              key={title}
              className="group rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-50 text-brand-700 transition-colors group-hover:bg-brand-100 dark:bg-brand-950 dark:text-brand-100">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-slate-100">
                {title}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {body}
              </p>
            </li>
          ))}
        </ul>
      </section>

      {/* Use cases */}
      <section className="border-t border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto max-w-5xl px-6 py-16">
          <h2 className="text-center text-3xl font-bold text-slate-900 dark:text-white">
            {t("landing.useCasesTitle")}
          </h2>
          <ul className="mx-auto mt-10 grid max-w-3xl gap-3 sm:grid-cols-2">
            {useCases.map((i) => (
              <li
                key={i}
                className="flex items-start gap-3 rounded-lg bg-white p-4 text-sm text-slate-700 shadow-sm dark:bg-slate-950 dark:text-slate-300"
              >
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                <span>{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-6 py-20 text-center">
        <h2 className="text-3xl font-bold text-slate-900 dark:text-white sm:text-4xl">
          {t("landing.finalCtaTitle")}
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-base text-slate-600 dark:text-slate-300">
          {t("landing.finalCtaBody")}
        </p>
        <Link
          to="/signup"
          className="btn-primary mt-8 inline-flex items-center gap-2 min-h-[52px] px-6 text-base"
        >
          {t("landing.ctaPrimary")}
          <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 dark:border-slate-800">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-4 px-6 py-10 text-xs text-slate-500 sm:flex-row sm:items-center">
          <p className="font-bold text-brand-700 dark:text-brand-100">
            {t("app.name")}{" "}
            <span className="ms-2 font-normal text-slate-500">
              — {t("landing.footerTagline")}
            </span>
          </p>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-slate-900 dark:hover:text-slate-100">
              {t("nav.signIn")}
            </Link>
            <Link to="/signup" className="hover:text-slate-900 dark:hover:text-slate-100">
              {t("nav.createAccount")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function EditorPreview() {
  const { t } = useTranslation();
  const boxes = [
    { x: 2, y: 2, w: 38, h: 30, color: "bg-blue-400" },
    { x: 41, y: 2, w: 28, h: 30, color: "bg-emerald-400" },
    { x: 70, y: 2, w: 27, h: 30, color: "bg-amber-400" },
    { x: 2, y: 34, w: 22, h: 24, color: "bg-violet-400" },
    { x: 25, y: 34, w: 22, h: 24, color: "bg-rose-400" },
    { x: 48, y: 34, w: 49, h: 24, color: "bg-cyan-400" },
    { x: 2, y: 60, w: 95, h: 37, color: "bg-orange-400" },
  ];
  return (
    <div className="flex h-[380px] flex-col text-sm">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full bg-red-400" />
          <div className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <div className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
          <span className="ms-3 text-xs text-slate-500">CutMate</span>
        </div>
        <span className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white">
          {t("editor.optimize")}
        </span>
      </div>

      <div className="grid flex-1 grid-cols-[110px_1fr_110px]">
        <div className="border-e border-slate-200 p-3 text-xs dark:border-slate-800">
          <p className="font-semibold text-slate-900 dark:text-slate-100">
            Parts
          </p>
          {["Side", "Door", "Shelf", "Back"].map((label) => (
            <div
              key={label}
              className="mt-2 rounded border border-slate-200 px-2 py-1 text-slate-600 dark:border-slate-700 dark:text-slate-300"
            >
              {label}
            </div>
          ))}
        </div>

        <div className="relative bg-slate-50 p-3 dark:bg-slate-900">
          <div className="relative h-full w-full rounded border border-slate-300 bg-white dark:border-slate-700 dark:bg-slate-950">
            {boxes.map((b, i) => (
              <div
                key={i}
                style={{
                  left: `${b.x}%`,
                  top: `${b.y}%`,
                  width: `${b.w}%`,
                  height: `${b.h}%`,
                }}
                className={`absolute rounded-sm border border-slate-900/30 ${b.color} opacity-90`}
              />
            ))}
          </div>
        </div>

        <div className="border-s border-slate-200 p-3 text-xs dark:border-slate-800">
          <p className="font-semibold text-slate-900 dark:text-slate-100">
            {t("summary.title")}
          </p>
          <dl className="mt-3 space-y-2">
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("summary.waste")}
              </dt>
              <dd className="font-mono text-lg font-bold text-slate-900 dark:text-slate-100">
                12.8%
              </dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">
                {t("summary.sheets")}
              </dt>
              <dd className="font-mono">2</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
